<template>
    <div class="modal add-episode-modal">
        <div class="modal-container">
            <base-modal-close @close-modal="$emit('close')" />

            <div class="modal-content">
                <div class="modal-header">
                    <div class="header-column">
                        <episode-modal-title
                            :context="addEpisodeModal.context"
                            :episode="episode"
                        />

                        <div
                            v-if="episodeModal.episodeExists()"
                            class="subtitle"
                        >
                            <publication-timestamp
                                :context="addEpisodeModal.context"
                                :published-at="initialPublicationDate"
                                :created-at="episode.createdAt"
                                @reschedule="rescheduleEpisode"
                            />

                            <button
                                v-if="null !== initialPublicationDate"
                                class="draft-episode"
                                @click="draftEpisode"
                            >
                                ({{ draftEpisodeText }})
                            </button>
                        </div>
                    </div>

                    <submit-buttons
                        :published-at="episodeModal.getEpisodeData('publishedAt')"
                        @submit="submitEpisode"
                    />
                </div>

                <add-episode-player :show="show" />

                <add-episode-nav v-model="tab" />

                <transition name="fade" mode="out-in">
                    <add-episode-details-tab
                        v-if="tab === 'details'"
                        :show="show"
                        @upload="file => $emit('upload', file)"
                        @cancel="$emit('cancel')"
                    />
                    <component
                        :is="currentTabComponentName"
                        v-else
                        :show="show"
                    />
                </transition>

                <div class="modal-footer">
                    <button
                        v-if="episodeModal.inEditContext()"
                        type="button"
                        class="delete-button"
                        @click.prevent="$emit('delete', episodeModal.getEpisodeData('id'))"
                    >
                        <i class="far fa-trash-alt delete-button-icon" />
                        <span class="delete-button-text">Delete episode</span>
                    </button>

                    <submit-buttons
                        class="footer-buttons"
                        :published-at="initialPublicationDate"
                        @submit="submitEpisode"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
<script>
// External dependencies.
import { mapState } from 'vuex';
import moment from 'moment-timezone';
// Internal dependencies.
import AddEpisodeDetailsTab from './AddEpisodeDetailsTab';
import AddEpisodeNav from './AddEpisodeNav';
import AddEpisodePlayer from './AddEpisodePlayer';
import AddEpisodeShareTab from './AddEpisodeShareTab';
import AddEpisodeTranscriptTab from './AddEpisodeTranscriptTab';
import EpisodeModalTitle from './EpisodeModalTitle';
import PublicationTimestamp from '@/components/Shows/Episodes/PublicationTimestamp';
import SubmitButtons from '@/components/Shows/Episodes/EpisodeModal/SubmitButtons';
import episodeModal from '@/utils/episodeModal';
import modal from '@/mixins/modal';
import toasts from '@/mixins/toasts';

const DEFAULT_TAB = 'details';

export default {
    components: {
        AddEpisodeDetailsTab,
        AddEpisodeNav,
        AddEpisodePlayer,
        AddEpisodeShareTab,
        AddEpisodeTranscriptTab,
        EpisodeModalTitle,
        PublicationTimestamp,
        SubmitButtons,
    },

    mixins: [
        modal,
        toasts,
    ],

    props: {
        show: {
            type: [Object, Boolean],
            default: () => ({}),
        },
    },

    data: () => ({
        tab: DEFAULT_TAB,
        episodeModal,
        initialPublicationDate: null,
    }),

    computed: {
        ...mapState({
            addEpisodeModal: state => state.UIState.addEpisodeModal,
            file: state => state.UIState.addEpisodeModal.file,
            episode: state => state.UIState.addEpisodeModal.episode,
        }),

        /**
         * Determines whether file is loaded or not.
         *
         * @returns {boolean} File is loaded or not?
         */
        hasFile() {
            return this.episode.file || this.file instanceof File;
        },

        /**
         * Gets the name of current tab.
         *
         * @returns {string} Name of current tab.
         */
        currentTabComponentName() {
            return `add-episode-${this.tab}-tab`;
        },

        /**
         * Computes text for draft button.
         *
         * @returns {string} Draft button text.
         */
        draftEpisodeText() {
            if (moment(this.initialPublicationDate).isAfter(moment())) {
                return 'unschedule';
            }

            return 'unpublish';
        },
    },

    /**
     * Action after components is mounted.
     */
    created() {
        this.initialPublicationDate = episodeModal.getEpisodeData('publishedAt');
    },

    methods: {
        /**
         * Handles episode form submit.
         */
        async submitEpisode() {
            if (! await episodeModal.validate()) {
                this.toastError({ message: { title: 'Please resolve errors in form.' } });

                return;
            }

            episodeModal.setSaveStatus('saving');

            this.$emit(episodeModal.episodeExists() ? 'update' : 'store');
        },

        /**
         * Reschedules episode.
         *
         * @param  {string} value Schedule date.
         */
        rescheduleEpisode(value) {
            this.initialPublicationDate = value;

            episodeModal.updateEpisode({ publishedAt: value });
            episodeModal.setSaveStatus('saving');

            this.$emit('update');
        },

        /**
         * Drafts episode.
         */
        draftEpisode() {
            this.initialPublicationDate = null;

            episodeModal.updateEpisode({ publishedAt: null });
            episodeModal.setSaveStatus('saving');

            this.$emit('update');
        },
    },
};
</script>
